// Character personality registry — the single source of truth for
// "who each character is" beyond their portrait. Voice lines, age-tier
// pairings, and weekly rotation live here so every surface (onboarding,
// dashboard, empty states, celebrations, emails) speaks with the same
// consistent character voice.
//
// Keep lines short, warm, and natural in both Arabic and English. Never
// translate literally — write each language from scratch.

export type CharacterCode =
  | "jad"
  | "julia"
  | "anas"
  | "talia"
  | "yousef"
  | "luna";

export type CharacterPersona = {
  code: CharacterCode;
  nameEn: string;
  nameAr: string;
  color: string;
  taglineEn: string;
  taglineAr: string;
  /** A short "hi, I'm…" line used in onboarding + character of the week. */
  hiEn: string;
  hiAr: string;
  /** Lines for empty/encouragement moments. Rotated randomly per render. */
  encourageEn: string[];
  encourageAr: string[];
  /** Lines shown after the child completes a story/quiz/challenge. */
  celebrateEn: string[];
  celebrateAr: string[];
};

export const PERSONAS: Record<CharacterCode, CharacterPersona> = {
  jad: {
    code: "jad",
    nameEn: "Jad",
    nameAr: "جاد",
    color: "#f59e0b",
    taglineEn: "Curious explorer",
    taglineAr: "مستكشف فضولي",
    hiEn: "Hi! I'm Jad. Want to discover something new today?",
    hiAr: "أهلًا! أنا جاد. تحب نكتشف شي جديد اليوم؟",
    encourageEn: [
      "Every question is a tiny door — let's open one!",
      "Curious minds find the best treasures.",
      "I love when you ask 'why?' Let's find out together.",
    ],
    encourageAr: [
      "كل سؤال باب صغير — يلا نفتح واحد!",
      "العقول الفضولية تلاقي أحلى الكنوز.",
      "بحب لما تسأل «ليش؟» — يلا نكتشف سوا.",
    ],
    celebrateEn: [
      "Wow! You're a real explorer now.",
      "I learned something today too — thanks!",
    ],
    celebrateAr: [
      "واو! صرت مستكشف حقيقي.",
      "أنا كمان تعلّمت شي اليوم — شكرًا!",
    ],
  },
  julia: {
    code: "julia",
    nameEn: "Julia",
    nameAr: "جوليا",
    color: "#ec4899",
    taglineEn: "Kindness & creativity",
    taglineAr: "لطف وإبداع",
    hiEn: "Hi! I'm Julia. Let's color the day with kindness.",
    hiAr: "مرحبًا! أنا جوليا. خلّينا نلوّن يومنا باللطف.",
    encourageEn: [
      "A small kind word can make a big smile.",
      "Your ideas are beautiful — try one!",
      "Drawing, singing, sharing… all of it counts.",
    ],
    encourageAr: [
      "كلمة لطيفة صغيرة بتعمل ابتسامة كبيرة.",
      "أفكارك حلوة — جرّب وحدة!",
      "الرسم والغناء والمشاركة… كلها مهمة.",
    ],
    celebrateEn: [
      "That was so creative! I'd hang it on my wall.",
      "Your heart is as bright as your colors.",
    ],
    celebrateAr: [
      "إبداع! بحب أعلّقها عندي.",
      "قلبك مضيء مثل ألوانك.",
    ],
  },
  anas: {
    code: "anas",
    nameEn: "Anas",
    nameAr: "أنس",
    color: "#10b981",
    taglineEn: "Thoughtful problem solver",
    taglineAr: "مفكّر يحلّ المشكلات",
    hiEn: "Hey, I'm Anas. Tricky puzzles? I love those.",
    hiAr: "أهلًا، أنا أنس. ألغاز صعبة؟ بحبها!",
    encourageEn: [
      "Take a breath, think it through — you've got this.",
      "Mistakes are clues. Use them.",
      "Slow and clever beats fast and unsure.",
    ],
    encourageAr: [
      "خذ نفس، فكّر بهدوء — تقدر تعملها.",
      "الأخطاء أدلّة — استفد منها.",
      "الهدوء والذكاء أحسن من العجلة.",
    ],
    celebrateEn: [
      "Clean thinking! That answer was sharp.",
      "I knew you'd figure it out.",
    ],
    celebrateAr: [
      "تفكير ممتاز! إجابة ذكية.",
      "كنت متأكد إنك راح تحلها.",
    ],
  },
  talia: {
    code: "talia",
    nameEn: "Talia",
    nameAr: "تاليا",
    color: "#8b5cf6",
    taglineEn: "Confidence & empathy",
    taglineAr: "ثقة وتعاطف",
    hiEn: "Hi! I'm Talia. Be brave — I'm right here with you.",
    hiAr: "مرحبًا! أنا تاليا. كن شجاع — أنا معك.",
    encourageEn: [
      "You can do hard things. I've seen you.",
      "Speak up — your voice matters.",
      "Be kind to yourself first, always.",
    ],
    encourageAr: [
      "تقدر تعمل أشياء صعبة — شفتك.",
      "احكي — صوتك مهم.",
      "كن لطيف مع نفسك أولًا، دايمًا.",
    ],
    celebrateEn: [
      "That took real courage. Proud of you.",
      "You made that look easy!",
    ],
    celebrateAr: [
      "هذي شجاعة حقيقية. فخورة فيك.",
      "خلّيتها تبان سهلة!",
    ],
  },
  yousef: {
    code: "yousef",
    nameEn: "Yousef",
    nameAr: "يوسف",
    color: "#7a8c5c",
    taglineEn: "Calm strength & quiet courage",
    taglineAr: "قوة هادئة وشجاعة صامتة",
    hiEn: "Hi, I'm Yousef. Real strength is quiet — and it starts inside.",
    hiAr: "أهلًا، أنا يوسف. القوة الحقيقية هادئة — وتبدأ من الداخل.",
    encourageEn: [
      "Courage isn't loud — it's showing up, gently.",
      "Take your time. Steady steps build strong roots.",
      "You're allowed to think before you speak. That's a strength.",
    ],
    encourageAr: [
      "الشجاعة مش صوت عالي — هي أنك تكمّل بهدوء.",
      "خذ وقتك. الخطوات الثابتة تبني جذورًا قوية.",
      "من حقك تفكر قبل ما تحكي — هذي قوة.",
    ],
    celebrateEn: [
      "Well done. That took real awareness.",
      "Quiet, thoughtful, brave. Proud of you.",
    ],
    celebrateAr: [
      "أحسنت. هذي صحوة حقيقية.",
      "هادئ، متأمّل، شجاع. فخور فيك.",
    ],
  },
  luna: {
    code: "luna",
    nameEn: "Luna",
    nameAr: "لونا",
    color: "#b8694b",
    taglineEn: "Reflection & warm wisdom",
    taglineAr: "تأمّل ودفء وحكمة",
    hiEn: "Hi, I'm Luna. Let's slow down and listen to what we really feel.",
    hiAr: "أهلًا، أنا لونا. خلّينا نهدأ ونصغي لما نشعر به حقًا.",
    encourageEn: [
      "Your feelings are signals — listen, don't fight them.",
      "Breathe. The right answer often comes after the pause.",
      "You don't have to figure everything out today.",
    ],
    encourageAr: [
      "مشاعرك إشارات — أصغِ لها، لا تقاومها.",
      "تنفّس. الإجابة الصحيحة بتجي بعد لحظة هدوء.",
      "مش لازم تفهم كل شي اليوم.",
    ],
    celebrateEn: [
      "Beautiful. You listened to yourself first.",
      "That kind of reflection is wisdom growing.",
    ],
    celebrateAr: [
      "جميل. أصغيت لنفسك أوّلًا.",
      "هذا التأمّل هو الحكمة وهي تكبر.",
    ],
  },
};

export const ORDERED_PERSONAS: CharacterPersona[] = [
  PERSONAS.jad,
  PERSONAS.julia,
  PERSONAS.anas,
  PERSONAS.talia,
  PERSONAS.yousef,
  PERSONAS.luna,
];

/** Map an onboarding age-tier to the pair of "best matched" characters. */
export function pairForTier(tier: "4-6" | "7-9" | "10-12"): [CharacterPersona, CharacterPersona] {
  if (tier === "4-6") return [PERSONAS.jad, PERSONAS.julia];
  if (tier === "7-9") return [PERSONAS.anas, PERSONAS.talia];
  return [PERSONAS.yousef, PERSONAS.luna];
}

/** Same map for an absolute age (used on the child dashboard). */
export function pairForAge(age: number): [CharacterPersona, CharacterPersona] {
  if (age <= 6) return pairForTier("4-6");
  if (age <= 9) return pairForTier("7-9");
  return pairForTier("10-12");
}

/** Deterministic ISO-week rotation — every child sees the same featured character that week. */
export function featuredCharacterOfWeek(date = new Date()): CharacterPersona {
  // Days since epoch / 7 → stable week index.
  const week = Math.floor(date.getTime() / (1000 * 60 * 60 * 24 * 7));
  return ORDERED_PERSONAS[week % ORDERED_PERSONAS.length];
}

/** Pick a deterministic encouragement line — keeps SSR + client in sync. */
export function pickEncouragement(p: CharacterPersona, isAr: boolean, seed: number): string {
  const list = isAr ? p.encourageAr : p.encourageEn;
  return list[Math.abs(seed) % list.length];
}
